/**
 * 「本月三件事」卡（**首页 · E 域 E2** · 批 `155`）
 *
 * 依据：`nucleus/insight.ts`（唯一算法）· `contract/insight.ts`（形状）·
 * `features/insight-labels.ts`（三段文案）· 规格 §15 / `DR-014` 决策四 / `DR-086`。
 *
 * ## 🔴 本组件**一个数都不算**
 *
 * | 显示 | 来源 |
 * |---|---|
 * | 有哪几条、谁排第一 | 服务端 `rankInsights()`（`isKey` 也由它标） |
 * | 每条结论里的金额 / 百分比 / 条数 | 服务端下发字段，经 `formatMoneyText` 排版 |
 * | 点「动作」去哪 | `features/insight-labels.ts` 的 `INSIGHT_ACTION_TARGETS` |
 *
 * ⇒ 本文件里没有 `reduce`、没有除法、没有日期算术。
 *
 * ## 🔴 三个边界态（缺一不可）
 *
 * | 态 | 判据 | 长什么样 |
 * |---|---|---|
 * | 正常 | `items.length > 0` | 至多 3 条，第 1 条 `is-key` 加重 |
 * | **不足 3 条** | `1 ≤ items.length < 3` | **只画已有的**，不补"暂无"占位卡（`AC-E2.6`） |
 * | 全空 | `isEmpty` | **正向空态**（"本月没有需要你处理的事"），**不画空列表**（`AC-E2.7`） |
 *
 * ⚠️ 「不足 3 条」与「正常」是同一段渲染代码 —— 差别只在条数。刻意不写两个分支：
 * 两个分支意味着两条渲染路径，而"补齐占位"往往就是在另一条路径里悄悄加进去的。
 */

import { Alert, Box, Button, Card, CardActionArea, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getMonthlyInsights } from '../api/endpoints.js';
import {
  INSIGHTS_EMPTY_HINT,
  INSIGHTS_EMPTY_TITLE,
  INSIGHTS_ERROR_PREFIX,
  INSIGHTS_KEY_BADGE,
  INSIGHTS_REFRESH_LABEL,
  INSIGHTS_SUBTITLE,
  INSIGHTS_TITLE,
  toInsightRows,
} from '../features/insight-labels.js';
import { Loading } from './Loading.js';

export function MonthlyInsightsCard() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['insights-monthly'],
    queryFn: getMonthlyInsights,
  });

  if (q.isPending) return <Loading py={3} />;
  if (q.isError) {
    return (
      <Alert severity="error" data-testid="insights-error">
        {INSIGHTS_ERROR_PREFIX}
        {String(q.error)}
      </Alert>
    );
  }

  const rows = toInsightRows(q.data);

  return (
    <Card data-testid="insights-card" data-anchor={INSIGHTS_TITLE}>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} data-testid="insights-title">
              {INSIGHTS_TITLE}
            </Typography>
            {/* 🔴 `AC-E2.11` 的固定文案（本地规则生成，未上传流水）—— 口径行写在标题旁 */}
            <Typography variant="caption" color="text.secondary" noWrap>
              {INSIGHTS_SUBTITLE}
            </Typography>
          </Stack>
          <Button
            size="small"
            data-testid="insights-refresh"
            onClick={() => void q.refetch()}
            // 刷新中禁用，避免连点造成并行请求
            disabled={q.isFetching}
          >
            {INSIGHTS_REFRESH_LABEL}
          </Button>
        </Stack>

        {/*
          🔴 `isEmpty` ⇒ 正向空态（`AC-E2.7`）。
          刻意**不渲染** `.things` 列表块 —— 一张"列表为空"的卡会让人以为加载失败或没记账。
        */}
        {q.data.isEmpty ? (
          <Box sx={{ py: 3, textAlign: 'center' }} data-testid="insights-empty">
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {INSIGHTS_EMPTY_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {INSIGHTS_EMPTY_HINT}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }} data-testid="insights-list">
            {rows.map((row, index) => (
              <Box key={row.key}>
                {index === 0 ? null : <Divider />}
                <CardActionArea
                  data-testid={`insights-item-${index}`}
                  data-kind={row.kind}
                  data-key={row.isKey ? '1' : '0'}
                  onClick={() => void navigate(row.actionTarget)}
                  sx={{
                    borderRadius: 1,
                    // 🔴 第 1 条视觉加重（`AC-E2.10`）：左侧竖条 + 略深底，不靠字号变大
                    ...(row.isKey
                      ? {
                          borderLeft: '3px solid',
                          borderColor: row.alarming ? 'error.main' : 'primary.main',
                          bgcolor: 'action.hover',
                          pl: 1,
                        }
                      : {}),
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'flex-start', py: 1, pr: 1 }}
                  >
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{ mt: 0.25, minWidth: 24, height: 20, flexShrink: 0 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        data-testid={`insights-item-title-${index}`}
                        sx={{
                          fontWeight: row.isKey ? 600 : 400,
                          // 铁律 8：只有「最高」档用朱红（红 = 预警）
                          color: row.alarming ? 'error.main' : 'text.primary',
                        }}
                      >
                        {row.title}
                      </Typography>
                      {/* 证据行：这个数字是哪来的（`AC-E2.8` 三段之二） */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        data-testid={`insights-item-evidence-${index}`}
                        sx={{ mt: 0.25 }}
                      >
                        {row.evidence}
                      </Typography>
                    </Box>
                    <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                      {row.isKey ? (
                        <Chip
                          label={INSIGHTS_KEY_BADGE}
                          size="small"
                          color={row.alarming ? 'error' : 'primary'}
                          variant="outlined"
                          data-testid="insights-key-badge"
                          sx={{ height: 20, mb: 0.5 }}
                        />
                      ) : null}
                      {/* 动作（`AC-E2.8` 三段之三）：一条建议必须能点走 */}
                      <Typography
                        variant="caption"
                        color="primary"
                        data-testid={`insights-item-action-${index}`}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {row.actionLabel} ›
                      </Typography>
                    </Stack>
                  </Stack>
                </CardActionArea>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
